import { useTranslation } from "react-i18next";

import { DeviceState } from "@devices/types";
import { StatusLabel, StatusTone } from "@shared/StatusLabel";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

type SecurityState = {
    tone: StatusTone;
    label: string;
    tooltip: string;
};

const SecurityValue = ({ state }: { state: SecurityState | null }) =>
    state ? (
        <StatusLabel tone={state.tone} tooltip={state.tooltip}>
            {state.label}
        </StatusLabel>
    ) : (
        <>-</>
    );

// Rows are ordered by how much harm the bad state allows, worst first.
export const SecuritySection = ({ deviceState }: { deviceState: DeviceState | undefined }) => {
    const { t } = useTranslation(["common", "sidepanels"]);

    const buildTags = deviceState?.build?.tags;
    const selinuxMode = deviceState?.security?.selinux;
    const adbValue = deviceState?.settings?.find((setting) => setting.key === "adb_enabled")?.value;
    const buildVariant = deviceState?.build?.type;

    const getCertificateState = (): SecurityState | null => {
        if (!buildTags) {
            return null;
        }
        return buildTags.includes("release-keys")
            ? {
                  tone: "positive",
                  label: t("sidepanels:devicePanel.osTab.certificateRelease"),
                  tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.certificate.release"),
              }
            : {
                  tone: "warning",
                  label: t("sidepanels:devicePanel.osTab.certificateTest"),
                  tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.certificate.test"),
              };
    };

    const getSelinuxState = (): SecurityState | null => {
        switch (selinuxMode) {
            case "enforcing":
                return {
                    tone: "positive",
                    label: t("sidepanels:devicePanel.osTab.selinuxMode.enforcing"),
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.selinux.enforcing"),
                };
            case "permissive":
                return {
                    tone: "neutral",
                    label: t("sidepanels:devicePanel.osTab.selinuxMode.permissive"),
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.selinux.permissive"),
                };
            case "disabled":
                return {
                    tone: "warning",
                    label: t("sidepanels:devicePanel.osTab.selinuxMode.disabled"),
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.selinux.disabled"),
                };
            default:
                return null;
        }
    };

    // Unlike other switches, an enabled ADB is the bad state and a disabled one the good state.
    const getAdbState = (): SecurityState | null => {
        if (!adbValue) {
            return null;
        }
        return adbValue === "0"
            ? {
                  tone: "positive",
                  label: t("common:label.disabled"),
                  tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.adb.disabled"),
              }
            : {
                  tone: "warning",
                  label: t("common:label.enabled"),
                  tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.adb.enabled"),
              };
    };

    const getBuildVariantState = (): SecurityState | null => {
        switch (buildVariant) {
            case undefined:
            case "":
                return null;
            case "user":
                return {
                    tone: "positive",
                    label: t("common:section.buildVariant.user.title"),
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.buildVariant.user"),
                };
            case "userdebug":
                return {
                    tone: "warning",
                    label: t("common:section.buildVariant.userdebug.title"),
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.buildVariant.debug"),
                };
            case "eng":
                return {
                    tone: "warning",
                    label: t("common:section.buildVariant.eng.title"),
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.buildVariant.debug"),
                };
            default:
                return {
                    tone: "neutral",
                    label: buildVariant,
                    tooltip: t("sidepanels:devicePanel.osTab.securityTooltip.buildVariant.unknown"),
                };
        }
    };

    return (
        <PaneElement.Section>
            <PaneElement.Header>{t("sidepanels:devicePanel.osTab.security")}</PaneElement.Header>
            <PropertyRow>
                <PropertyKey>{t("sidepanels:devicePanel.osTab.certificate")}</PropertyKey>
                <PropertyValue>
                    <SecurityValue state={getCertificateState()} />
                </PropertyValue>
            </PropertyRow>
            <PropertyRow>
                <PropertyKey>{t("sidepanels:devicePanel.osTab.selinux")}</PropertyKey>
                <PropertyValue>
                    <SecurityValue state={getSelinuxState()} />
                </PropertyValue>
            </PropertyRow>
            <PropertyRow>
                <PropertyKey>{t("sidepanels:devicePanel.osTab.adbStatus")}</PropertyKey>
                <PropertyValue>
                    <SecurityValue state={getAdbState()} />
                </PropertyValue>
            </PropertyRow>
            <PropertyRow>
                <PropertyKey>{t("common:section.buildVariant.header")}</PropertyKey>
                <PropertyValue>
                    <SecurityValue state={getBuildVariantState()} />
                </PropertyValue>
            </PropertyRow>
        </PaneElement.Section>
    );
};
